/* eslint-disable prefer-arrow-callback */
import React, { useEffect, memo } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'
import {
  actions,
  updateThirdPartyIntercom,
  getThirdPartyIntercom,
} from 'features/Intercoms/intercomsSlice'
import { editDevice } from 'features/Devices/devicesSlice'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  FormModal,
  FormModalActions,
  FormModalContent,
  FormModalTitle,
} from 'sputnik-ui'
import * as yup from 'yup'
import 'utils/thirdPartyYup'
import InputWithError from 'components/atoms/InputWithError'
import {
  THIRD_PARTY_VENDORS,
  deviceVendor,
  passwordSchema,
  vendorConfig,
} from 'utils/thirdPartyIntercomKinds'

export const EditThirdPartyIntercom = memo(function EditThirdPartyIntercom({
  open,
  onClose,
  uuid: intercomId,
}) {
  const { t } = useTranslation(['devices', 'common'])
  const companyUuid = useSelector(({ auth }) => auth.user?.company?.uuid)
  const { current, pending } = useSelector(({ intercoms }) => intercoms)
  const dispatch = useDispatch()

  // intercom_kind приходит из /foreign_intercoms как BEWARD/ELTEX. У старых записей Beward
  // он мог потеряться при редактировании — такие считаем панелями Beward.
  const vendorKey = deviceVendor(current?.intercom_kind) || 'beward'
  const vendor = vendorConfig(vendorKey) || THIRD_PARTY_VENDORS.beward

  const schema = yup.object().shape({
    alias: yup.string().nullable().max(60),
    rtsp_link: vendor.hasCamera
      ? yup
          .string()
          .rtspLink(t('common:field.error.rtspLink'))
          .required(t('common:field.error.required'))
      : yup.string().nullable().notRequired(),
    login: yup.string().required(t('common:field.error.required')),
    password: passwordSchema(yup.string(), vendorKey, t),
    ip_address:
      vendor.address === 'host'
        ? yup
            .string()
            .hostSocket(t('common:field.error.wrong_host'))
            .required(t('common:field.error.required'))
        : yup
            .string()
            .ipv4Socket(t('common:field.error.wrong_ip'))
            .required(t('common:field.error.required')),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      uuid: intercomId,
      company_uuid: companyUuid || '',
      rtsp_link: current?.rtsp_link || '',
      alias: current?.alias || '',
      login: current?.login || '',
      password: current?.password || '',
      ip_address: current?.ip_address || '',
    },
    validationSchema: schema,
    onSubmit: (val) => {
      const { rtsp_link, ...rest } = val
      const payload = vendor.hasCamera ? { ...rest, rtsp_link } : rest
      // Список обновляем только после успешного сохранения: иначе у карандаша
      // осталась бы несохранённая заметка. Ошибку показывает сам thunk.
      dispatch(updateThirdPartyIntercom(payload))
        .then(unwrapResult)
        .then(() => {
          onClose()
          dispatch(
            editDevice({ deviceUuid: val.uuid, fields: { alias: val.alias } })
          )
        })
        .catch(() => {})
    },
  })

  useEffect(() => {
    if (intercomId) {
      dispatch(getThirdPartyIntercom(intercomId))
    }
    return () => dispatch(actions.clearIntercomsStore())
  }, [intercomId, dispatch])

  return (
    <>
      <FormModal open={open}>
        <FormModalTitle>{t('devices:intercoms.edit')}</FormModalTitle>
        <FormModalContent label={t('devices:intercoms.third_party.edit_info')}>
          <InputWithError
            name="ip_address"
            formik={formik}
            label={t(
              vendor.address === 'host'
                ? 'devices:intercoms.third_party.address_host'
                : 'devices:intercoms.third_party.ip_address'
            )}
            required={false}
            disabled={pending}
          />
          <InputWithError
            name="login"
            formik={formik}
            label={t('devices:intercoms.third_party.login')}
            required={false}
            disabled={pending}
          />
          <InputWithError
            name="password"
            formik={formik}
            label={t('devices:intercoms.third_party.password')}
            required={false}
            disabled={pending}
          />

          {vendor.hasCamera && (
            <InputWithError
              name="rtsp_link"
              formik={formik}
              label={t('devices:cameras.third_party.rtsp')}
              required={false}
              style={{
                marginTop: 5,
              }}
              disabled={pending}
            />
          )}
          <InputWithError
            name="alias"
            formik={formik}
            label={t('devices:cameras.third_party.alias')}
            required={false}
            style={{ marginBottom: 24 }}
            disabled={pending}
          />
        </FormModalContent>
        <FormModalActions>
          <Button
            variant="v2"
            primary
            fullWidth
            type="submit"
            onClick={formik.handleSubmit}
            disabled={Object.keys(formik.errors).length > 0 || pending}
          >
            {t('common:button.save_edit')}
          </Button>
          <Button fullWidth variant="v2" tertiary onClick={onClose}>
            {t('common:button.back')}
          </Button>
        </FormModalActions>
      </FormModal>
    </>
  )
})
